import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { trackPurchase, makeVariantSku } from "@/lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired | error
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const pollCount = useRef(0);
  const cleared = useRef(false);
  const purchaseFired = useRef(false);

  useEffect(() => {
    if (state !== "paid" || !order || purchaseFired.current) return;
    const lines = order.lines || [];
    trackPurchase({
      content_ids: lines.map((l) => makeVariantSku(l.product_id, l.color, l.size)),
      num_items: lines.reduce((n, l) => n + (l.quantity || 1), 0),
      value: order.total_usd ?? 0,
      currency: "AUD",
    });
    purchaseFired.current = true;
  }, [state, order]);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollCount.current += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const ps = res?.data?.payment_status;
        const st = res?.data?.status;
        if (ps === "paid") {
          setState("paid");
          setOrder(res.data.order);
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (st === "expired") {
          setState("expired");
          return;
        }
        if (pollCount.current >= 8) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        console.warn("[checkout] status poll failed (attempt", pollCount.current, "):", e);
        if (pollCount.current >= 8) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="px-4 md:px-8 py-12 md:py-20 min-h-[70vh]" data-testid="success-page">
      <div className="max-w-2xl mx-auto border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-6 md:p-10">
        {state === "checking" && (
          <div className="text-center" data-testid="success-checking">
            <Loader2 size={40} className="mx-auto animate-spin" />
            <p className="font-display text-3xl uppercase mt-4">Checking your sad order…</p>
            <p className="font-body text-sm text-[#1C1914]/70 mt-2">Hang tight, we're verifying with Stripe.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="success-paid">
            <CheckCircle2 size={48} className="text-[#4A5D23]" />
            <p className="font-condensed text-sm uppercase tracking-widest text-[#D95D39] mt-4">
              // Order confirmed
            </p>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none mt-2">
              Thanks, you have made a poor choice.
            </h1>
            <p className="font-body text-base mt-4 text-[#1C1914]/85">
              Receipt is on its way to your inbox. We'll begin printing approximately whenever we feel like it.
            </p>

            {order && (
              <div className="border-t-2 border-dashed border-[#1C1914]/40 mt-6 pt-6" data-testid="success-order-summary">
                <p className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]/70 mb-3">
                  What you bought (regrettably)
                </p>
                <div className="space-y-2">
                  {order.lines?.map((l, i) => (
                    <div key={i} className="flex items-center justify-between font-body text-base">
                      <span>
                        {l.product_name}{l.color ? ` — ${l.color}` : ""} — Size {l.size} × {l.quantity}
                      </span>
                      <span className="font-display">
                        A${(l.unit_price_usd * l.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t-2 border-dashed border-[#1C1914]/40 space-y-1 font-body text-sm">
                  <div className="flex items-center justify-between">
                    <span>Subtotal</span>
                    <span>A${(order.subtotal_usd ?? 0).toFixed(2)}</span>
                  </div>
                  {(order.discount_usd ?? 0) > 0 && (
                    <div className="flex items-center justify-between text-[#D95D39]" data-testid="success-discount-row">
                      <span>{order.promo_label || "Promo"}</span>
                      <span>-A${(order.discount_usd ?? 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span>{(order.shipping_usd ?? 0) === 0 ? "Free shipping" : "Shipping (flat)"}</span>
                    <span>A${(order.shipping_usd ?? 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t-2 border-[#1C1914] font-display text-2xl">
                  <span>Total</span>
                  <span>A${order.total_usd?.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/" className="btn-duds flex-1 text-center" data-testid="success-continue-shopping">
                Keep shopping
              </Link>
            </div>
          </div>
        )}

        {(state === "expired" || state === "error") && (
          <div data-testid="success-failed">
            <AlertTriangle size={48} className="text-[#D95D39]" />
            <h1 className="font-display text-4xl uppercase mt-4">
              {state === "expired" ? "Session expired." : "Hmm."}
            </h1>
            <p className="font-body text-base mt-3 text-[#1C1914]/85">
              {state === "expired"
                ? "Your checkout session timed out. No charge was made."
                : "We couldn't confirm the payment from here. Check your email for a receipt — if you got one, you're good."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link to="/" className="btn-duds flex-1 text-center" data-testid="success-retry">
                Back to shop
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
